import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaRocket,
  FaUsers,
  FaAward,
  FaLightbulb,
  FaShieldAlt,
  FaChartLine,
  FaHandshake,
  FaCogs,
  FaClipboardCheck,
  FaHeadset,
  FaArrowRight
} from 'react-icons/fa';
import './About.css';
import companyImage from '../assets/images/company-image.png';

const About = () => {
  useEffect(() => {
    // Add scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in, .fade-up, .scale-up, .slide-in-left, .slide-in-right, .slide-in-blur-left, .slide-in-blur-right');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const coreValues = [
    {
      icon: <FaLightbulb />,
      title: "Innovation",
      description: "We constantly push the boundaries of what's possible, embracing new technologies and creative thinking to solve complex industrial challenges."
    },
    {
      icon: <FaShieldAlt />,
      title: "Quality",
      description: "We maintain high engineering and execution standards, ensuring every solution is robust, reliable, and built for long-term performance."
    },
    {
      icon: <FaHandshake />,
      title: "Partnership",
      description: "We believe in working as partners, not vendors—aligning our solutions with our customers' goals and supporting them through every stage of the journey."
    },
    {
      icon: <FaChartLine />,
      title: "Excellence",
      description: "We strive for excellence in every project, focusing on outcomes that drive measurable improvements in productivity and efficiency."
    },
    {
      icon: <FaUsers />,
      title: "Teamwork",
      description: "We foster a collaborative environment where diverse skills across mechanical, electrical, software, and robotics come together to deliver complete solutions."
    },
    {
      icon: <FaAward />,
      title: "Integrity",
      description: "We conduct business with honesty, transparency, and respect, building trust through our commitments and the way we deliver on them."
    }
  ];

  const teamMembers = [
    {
      icon: <FaCogs />,
      title: "Engineering Team",
      role: "Automation, Robotics & Software Engineers",
      description: "Our engineering team brings expertise across mechanical design, control systems, embedded electronics, robotics, and industrial software to build complete automation solutions end-to-end."
    },
    {
      icon: <FaClipboardCheck />,
      title: "Test & Validation Specialists",
      role: "PCB Test Jigs & Quality Systems",
      description: "Specialists in ICT/FCT PCB test jigs, fixtures, inspection systems, and validation processes that ensure every solution performs reliably in real production environments."
    },
    {
      icon: <FaHeadset />,
      title: "Project & Customer Success",
      role: "Implementation, Training & Support",
      description: "Experienced project and customer success teams who coordinate design, installation, training, and long-term support so that our solutions integrate smoothly into your existing operations."
    }
  ];

  const achievements = [
    {
      number: "70+",
      title: "Automation Projects",
      description: "Delivered industrial automation, robotics, test jigs, and software solutions across multiple sectors."
    },
    {
      number: "45+",
      title: "Industrial Customers",
      description: "Long-term partnerships with manufacturers and enterprises who trust us to automate and scale their operations."
    },
    {
      number: "2016",
      title: "Year Founded",
      description: "Nearly a decade of experience building practical, shop-floor-ready automation solutions."
    }
  ];

  return (
    <div className="about">
      <SEO
        title="About Robolog Automation - Industrial Automation Company Since 2016"
        description="Learn about Robolog Automation, a leading industrial automation company delivering turnkey solutions in robotics, SCADA, PCB test jigs, and smart manufacturing software since 2016. 70+ projects, 45+ customers, proven expertise."
        keywords="robolog automation company, about robolog, industrial automation company India, automation engineering firm, robotics company, PCB test jig manufacturer, SCADA solutions provider, industrial IoT company, manufacturing automation experts"
        ogImage="/company-image.png"
      />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content fade-up">
            <h1>About Robolog Automation</h1>
            <p>
              Robolog Automation is a turnkey industrial automation company delivering custom machines,
              robotics, control systems, and intelligent software that help manufacturers improve
              productivity, quality, and real-time visibility.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="company-story">
        <div className="container">
          <div className="story-content">
            <div className="story-text slide-in-blur-left">
              <h2>Our Story</h2>
              <p>
                Founded in 2016 with a vision to transform industrial operations, Robolog Automation
                started as a small team of passionate engineers who believed that manufacturing could
                be made more efficient, reliable, and data-driven.
              </p>
              <p>
                Today, we deliver turnkey automation solutions that combine industrial control systems,
                robotics and special purpose machines, high-precision PCB test jigs, and industrial
                software platforms. Our journey has been shaped by continuous innovation, close
                collaboration with our customers, and a commitment to building practical solutions
                that deliver measurable results on the shop floor.
              </p>
            </div>
            <div className="story-image scale-up">
              <img
                src={companyImage}
                alt="Robolog Automation Company"
                className="company-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card slide-in-blur-left">
              <div className="mv-icon">
                <FaRocket />
              </div>
              <h3>Our Mission</h3>
              <p>
                To empower manufacturers with end-to-end automation solutions that increase throughput,
                improve quality, and unlock real-time insights by combining intelligent machines,
                robust control systems, and industrial software.
              </p>
            </div>
            <div className="mv-card slide-in-blur-right">
              <div className="mv-icon">
                <FaLightbulb />
              </div>
              <h3>Our Vision</h3>
              <p>
                To be a trusted automation partner for industries worldwide, recognized for our ability
                to design and deliver practical, scalable solutions that bridge the gap between the
                physical shop floor and the digital world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide everything we do and shape our company culture.
            </p>
          </div>

          <div className="values-grid">
            {coreValues.map((value, index) => (
              <div key={index} className="value-card fade-up" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="value-icon">
                  {value.icon}
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Meet Our Team</h2>
            <p className="section-subtitle">
              Talented professionals dedicated to delivering exceptional automation solutions.
            </p>
          </div>

          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div key={index} className={`team-member fade-up`} style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="member-photo">
                  {member.icon}
                </div>
                <h3>{member.title}</h3>
                <p className="member-role">{member.role}</p>
                <p>{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="achievements">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Our Achievements</h2>
            <p className="section-subtitle">
              Milestones that reflect our commitment to excellence and innovation.
            </p>
          </div>

          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <div key={index} className={`achievement-item fade-up`} style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="achievement-number">{achievement.number}</div>
                <div className="achievement-title">{achievement.title}</div>
                <div className="achievement-description">{achievement.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="text-center scale-up">
            <h2>Ready to Work With Us?</h2>
            <p>
              Let's discuss how our expertise in turnkey industrial automation, robotics,
              PCB test jigs, and industrial software can help transform your operations
              and unlock new levels of productivity.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get In Touch
                <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-outline">
                View Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
